import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, m } from 'motion/react';
import { DURACAO, EASE_SAIDA, MOLA_PAINEL, SO_OPACIDADE, useMovimentoReduzido } from '../../lib/movimento';
import { Icone } from '../Icone';

type Tom = 'sucesso' | 'erro' | 'info';
type Aviso = { id: number; texto: string; tom: Tom };
type Avisar = (texto: string, tom?: Tom) => void;

const Contexto = createContext<Avisar | null>(null);

/** Tempo de leitura do aviso. Pausa com o ponteiro em cima ou foco dentro. */
const TEMPO_LEITURA_MS = 7000;

/**
 * Avisos curtos ("Endereço salvo"). Uma região viva só para o app inteiro:
 * várias regiões disputando o leitor de tela fazem anúncios se atropelarem.
 * Erro vai em `assertive`, o resto em `polite`. Todo aviso tem botão Fechar e
 * espera a pessoa enquanto o ponteiro ou o foco estiverem nele (WCAG 2.2.1).
 */
export function Avisador({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const proximo = useRef(1);
  const reduzido = useMovimentoReduzido();

  const avisar = useCallback<Avisar>((texto, tom = 'sucesso') => {
    const id = proximo.current++;
    setAvisos((lista) => [...lista.slice(-2), { id, texto, tom }]);
  }, []);
  const fechar = useCallback((id: number) => setAvisos((l) => l.filter((a) => a.id !== id)), []);

  const educados = avisos.filter((a) => a.tom !== 'erro');
  const urgentes = avisos.filter((a) => a.tom === 'erro');

  return (
    <Contexto.Provider value={avisar}>
      {children}
      <div className="avisador">
        {[['assertive', urgentes], ['polite', educados]].map(([modo, lista]) => (
        <ol key={modo as string} className="avisador-lista" aria-live={modo as 'polite' | 'assertive'} aria-relevant="additions">
          <AnimatePresence initial={false}>
            {(lista as Aviso[]).map((a) => (
              <m.li
                key={a.id}
                layout={!reduzido}
                initial={{ opacity: 0, y: reduzido ? 0 : 16, scale: reduzido ? 1 : 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, transition: reduzido ? SO_OPACIDADE : { duration: DURACAO.saida, ease: EASE_SAIDA } }}
                transition={reduzido ? SO_OPACIDADE : MOLA_PAINEL}
              >
                <ItemAviso aviso={a} aoFechar={fechar} />
              </m.li>
            ))}
          </AnimatePresence>
        </ol>
      ))}
      </div>
    </Contexto.Provider>
  );
}

function ItemAviso({ aviso, aoFechar }: { aviso: Aviso; aoFechar: (id: number) => void }) {
  const [pausado, setPausado] = useState(false);
  useEffect(() => {
    if (pausado) return;
    const t = window.setTimeout(() => aoFechar(aviso.id), TEMPO_LEITURA_MS);
    return () => window.clearTimeout(t);
  }, [pausado, aviso.id, aoFechar]);

  return (
    <div
      className={`avisador-item avisador-${aviso.tom}`}
      onPointerEnter={() => setPausado(true)}
      onPointerLeave={() => setPausado(false)}
      onFocus={() => setPausado(true)}
      onBlur={() => setPausado(false)}
    >
      <Icone nome={aviso.tom === 'erro' ? 'erro' : aviso.tom === 'info' ? 'info' : 'sucesso'} />
      <p>{aviso.texto}</p>
      <button type="button" className="acao-link" onClick={() => aoFechar(aviso.id)}>
        <Icone nome="fechar" />
        <span className="so-leitor">Fechar aviso</span>
      </button>
    </div>
  );
}

/**
 * Mostra um aviso curto. Fora do Avisador (testes isolados), não faz nada.
 * Com um Dialogo modal aberto o resto da página fica inerte, inclusive esta
 * região: erro de dentro do diálogo vai num <AvisoErro> no próprio diálogo.
 */
export function useAvisar(): Avisar {
  return useContext(Contexto) ?? (() => {});
}
